import {FC, useState, useEffect} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {useLazyQuery} from 'hooks/graphql'
import {useKeywordInput} from 'hooks/useKeywordInput'
import {fullNameFormat, displayFormat, dateFormat} from 'libs/formatter'
import {Select} from 'jbc-front/components/presenters/form/Select'
import Paginator from 'jbc-front/components/Paginator'

import {TitleContainer} from 'components/layout/TitleContainer'
import {MainTitle} from 'components/ui/MainTitle'
import {LoadingPage} from 'components/ui/LoadingPage'
import {KeywordSearch} from 'components/ui/KeywordSearch'
import {SearchResult} from 'components/ui/SearchResult'
import {PerPage} from 'components/ui/PerPage'
import {Table} from 'components/ui/Table'
import {LinkTab} from '../../show/parts/LinkTab'
import {Row} from './parts/Row'
import {CsvDownloadModal} from './parts/CsvDownloadModal'
import {RemindModal} from './parts/RemindModal'
import {CancelModal} from './parts/CancelModal'
import {
  CONTRACT,
  QueryResult,
  QueryVariables,
  Order,
  SortColumn,
  User,
  EmployeeDocumentContractRequest,
  Employee,
  Document,
  ContractInfo as ContractInfoType,
} from './query'
import styles from './Presenter.scss'

export const fullName = (user: User) => {
  if (user) {
    if (user.employee) {
      const {displayLastName, displayFirstName} = user.employee
      return fullNameFormat(displayLastName, displayFirstName)
    }

    return user.email
  }

  return displayFormat()
}

const contractRequestLabel = (request: EmployeeDocumentContractRequest) =>
  `${dateFormat(request.startedAt, 'YYYY/MM/DD HH:mm')} （送信者 ${request.contractInfo.senderName}）`

interface ContractInfoProps {
  contractInfo?: ContractInfoType
}

const ContractInfo: FC<ContractInfoProps> = ({contractInfo}) => (
  <div className="u-mb20">
    <div className={styles.contractInfoBox}>
      <div className={styles.title}>依頼内容</div>
      <div className={styles.row}>
        <div className={styles.rowHeader}>差出人：</div>
        <div className={styles.rowValue}>
          {contractInfo?.senderOfficeName} {contractInfo?.senderName}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.rowHeader}>書類名：</div>
        <div className={styles.rowValue}>{contractInfo?.title}</div>
      </div>
      {contractInfo?.comment && (
        <div className={styles.row}>
          <div className={styles.rowHeader}>コメント：</div>
          <div className={styles.rowValueComment}>{contractInfo?.comment}</div>
        </div>
      )}
    </div>
  </div>
)

interface PresenterProps {
  employee: Employee
  contracts: EmployeeDocumentContractRequest[]
}

export const Presenter: FC<PresenterProps> = ({employee, contracts}) => {
  const history = useHistory()
  const {contractRequestId} = useParams<{contractRequestId: string}>()

  const [sortOrder, setSortOrder] = useState<Order>('desc')
  const [sortColumn, setSortColumn] = useState<SortColumn>('created_at')
  const [keyword, setKeyword] = useState('')

  const [variables, setVariables] = useState<QueryVariables>({
    employeeId: employee.id,
    requestId: contractRequestId,
    page: 1,
    per: 10,
    search: {
      sortOrder,
      sortColumn,
    },
  })

  const [fetchContractRequest, {loading, data}] = useLazyQuery<QueryResult, QueryVariables>(CONTRACT)

  const contractRequestOptions = contracts.map((contractRequest) => ({
    value: contractRequest.id,
    label: contractRequestLabel(contractRequest),
  }))
  const contractRequest = data?.client?.employee?.employeeDocumentContractRequest
  const contractInfo = contractRequest?.contractInfo

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      const nextOrder = inverseOrder()
      setVariables((prev) => ({
        ...prev,
        page: 1,
        search: {
          ...prev.search,
          sortOrder: nextOrder,
        },
      }))
      setSortOrder(nextOrder)
    } else {
      setVariables((prev) => ({
        ...prev,
        page: 1,
        search: {
          ...prev.search,
          sortOrder: 'desc',
          sortColumn: column,
        },
      }))
      setSortOrder('desc')
      setSortColumn(column)
    }
  }

  const inverseOrder = () => {
    switch (sortOrder) {
      case 'desc':
        return 'asc'
      case 'asc':
        return 'desc'
    }
  }

  const handleInputKeyword = (value: string) => {
    setVariables((prev) => ({...prev, page: 1, search: {...prev.search, q: value}}))
  }
  const [, keywordSubject] = useKeywordInput(handleInputKeyword)

  const handleChangeRequest = (requestId: string) => {
    setKeyword('')
    history.push(`/documents/employees/${employee.id}/contracts/${requestId}`)
  }

  useEffect(() => {
    fetchContractRequest({variables})
  }, [variables])

  useEffect(() => {
    setVariables((prev) => ({
      ...prev,
      requestId: contractRequestId,
      page: 1,
      search: {
        sortOrder: prev.search.sortOrder,
        sortColumn: prev.search.sortColumn,
      },
    }))
  }, [contractRequestId])

  return (
    <>
      <TitleContainer className={styles.withLinkTabTitle}>
        <MainTitle>{`${employee.displayLastName} ${employee.displayFirstName}`}さんの書類</MainTitle>
        <LinkTab isShowContractTab isSentContract employeeId={employee.id} contractRequestId={contractRequestId} />
      </TitleContainer>
      <div className="l-wrap-xxl l-contents-wrap">
        <div className={styles.selectArea}>
          <Select
            options={contractRequestOptions}
            value={contractRequestOptions.find((opt) => opt.value === contractRequestId)}
            onChange={(newRequest) => handleChangeRequest(newRequest.value)}
          />
        </div>

        <>
          <ContractInfo contractInfo={contractInfo} />

          {contractInfo && (
            <div className={styles.buttonArea}>
              <CsvDownloadModal employeeId={employee.id} contractRequestId={contractRequestId} q={variables.search.q} />

              {contractInfo.status === 'unsigned' && (
                <>
                  <RemindModal
                    employeeId={employee.id}
                    contractRequestId={contractRequestId}
                    contractOriginalSenderInfo={contractInfo.contractOriginalSenderInfo}
                  />
                  <CancelModal employeeId={employee.id} contractRequestId={contractRequestId} />
                </>
              )}
            </div>
          )}

          <div className={styles.searchArea}>
            <KeywordSearch
              className={styles.keywordSearch}
              keyword={keyword}
              placeholder="書類名、作成者"
              onChange={(value) => {
                keywordSubject.next(value)
                setKeyword(value)
              }}
            />
          </div>

          {contractRequest && (
            <div className="u-mb20">
              <SearchResult
                total={contractRequest.searchDocuments.totalCount}
                limit={variables.per}
                page={variables.page}
              />
            </div>
          )}

          {loading ? (
            <LoadingPage />
          ) : (
            <div className="l-overflow-scroll">
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.SortableTh
                      onClick={() => handleSort('created_at')}
                      order={sortOrder}
                      active={sortColumn == 'created_at'}
                      width="15%"
                    >
                      作成日
                    </Table.SortableTh>

                    <Table.SortableTh
                      onClick={() => handleSort('title')}
                      order={sortOrder}
                      active={sortColumn == 'title'}
                      width="30%"
                    >
                      書類名
                    </Table.SortableTh>

                    <Table.SortableTh
                      onClick={() => handleSort('created_user')}
                      order={sortOrder}
                      active={sortColumn == 'created_user'}
                      width="35%"
                    >
                      作成者
                    </Table.SortableTh>

                    <Table.Th width="10%">ステータス</Table.Th>

                    <Table.Th width="10%">ダウンロード</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {contractInfo &&
                    contractRequest.searchDocuments.list.map((document: Document) => (
                      <Row key={document.id} document={document} employee={employee} contractInfo={contractInfo} />
                    ))}
                </Table.Tbody>
              </Table>
            </div>
          )}

          {contractRequest && (
            <div className="m-pager-area">
              <Paginator
                current={variables.page}
                rowsPerPage={variables.per}
                onClick={(page: number) => setVariables((prev) => ({...prev, page}))}
                totalResult={contractRequest.searchDocuments.totalCount}
              />
              <PerPage
                options={[10, 20, 50, 99]}
                selected={variables.per}
                onChange={(per) => setVariables((prev) => ({...prev, page: 1, per}))}
              />
            </div>
          )}
        </>
      </div>
    </>
  )
}
